import { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import {
  hubService,
  type RagCategory,
  type CreateRagCategoryDto,
  type DocumentType,
} from '../../../services/hub.service';
import { AiConfigTabShell, AiConfigTable } from '../index';

const DOCUMENT_TYPES: DocumentType[] = ['manual', 'tutorial', 'rule', 'product', 'faq', 'other'];

export function RagCategoriesTab() {
  const { getToken } = useAuth();
  const [items, setItems] = useState<RagCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<RagCategory | null>(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState<CreateRagCategoryDto>({
    key: '',
    label: '',
    description: '',
    documentTypes: [],
    isActive: true,
    sortOrder: 0,
  });

  useEffect(() => {
    load();
  }, []);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const token = getToken();
      const data = await hubService.listRagCategories(false, token);
      setItems(data);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }

  async function handleCreate() {
    if (!form.key.trim()) return;
    try {
      const token = getToken();
      await hubService.createRagCategory(
        {
          ...form,
          label: form.label || form.key,
          documentTypes: form.documentTypes?.length ? form.documentTypes : undefined,
        },
        token,
      );
      setCreating(false);
      setForm({ key: '', label: '', description: '', documentTypes: [], isActive: true, sortOrder: 0 });
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Create failed');
    }
  }

  async function handleUpdate(id: string) {
    if (!editing) return;
    try {
      const token = getToken();
      await hubService.updateRagCategory(
        id,
        {
          key: editing.key,
          label: editing.label,
          description: editing.description,
          documentTypes: editing.documentTypes ?? undefined,
          isActive: editing.isActive,
          sortOrder: editing.sortOrder,
        },
        token,
      );
      setEditing(null);
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Update failed');
    }
  }

  async function handleDelete(id: string) {
    if (!confirm('Delete this category?')) return;
    try {
      const token = getToken();
      await hubService.deleteRagCategory(String(id), token);
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Delete failed');
    }
  }

  function toggleDocType(dt: DocumentType, list: DocumentType[]) {
    const next = list.includes(dt) ? list.filter((x) => x !== dt) : [...list, dt];
    return next;
  }

  return (
    <AiConfigTabShell loading={loading} error={error}>
      <div className="space-y-6">
        {!creating ? (
          <button
            onClick={() => setCreating(true)}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md"
          >
            Add RAG Category
          </button>
        ) : (
          <div className="bg-white p-4 rounded-lg border border-gray-200 space-y-3">
            <input
              placeholder="Key (slug, e.g. installation)"
              value={form.key}
              onChange={(e) => setForm((f) => ({ ...f, key: e.target.value }))}
              className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
            />
            <input
              placeholder="Label"
              value={form.label}
              onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))}
              className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
            />
            <textarea
              placeholder="Description"
              value={form.description}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
              rows={2}
            />
            <div>
              <label className="block text-sm text-gray-600 mb-1">Document types (empty = all)</label>
              <div className="flex flex-wrap gap-2">
                {DOCUMENT_TYPES.map((dt) => (
                  <label key={dt} className="flex items-center gap-1 text-sm">
                    <input
                      type="checkbox"
                      checked={(form.documentTypes ?? []).includes(dt)}
                      onChange={() =>
                        setForm((f) => ({
                          ...f,
                          documentTypes: toggleDocType(dt, f.documentTypes ?? []),
                        }))
                      }
                    />
                    {dt}
                  </label>
                ))}
              </div>
            </div>
            <div className="flex gap-2">
              <button onClick={handleCreate} className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded">
                Save
              </button>
              <button onClick={() => setCreating(false)} className="px-3 py-1.5 text-sm bg-gray-200 rounded">
                Cancel
              </button>
            </div>
          </div>
        )}

        <AiConfigTable>
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Key</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Label</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Doc types</th>
              <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {items.map((item) => (
              <tr key={item.id}>
                <td className="px-4 py-2 text-sm font-mono">{item.key}</td>
                <td className="px-4 py-2 text-sm">{item.label}</td>
                <td className="px-4 py-2 text-sm">
                  {!item.documentTypes || item.documentTypes.length === 0 ? 'all' : item.documentTypes.join(', ')}
                </td>
                <td className="px-4 py-2 text-right">
                  <button
                    onClick={() => setEditing(item)}
                    className="text-blue-600 hover:underline text-sm mr-2"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(String(item.id))}
                    className="text-red-600 hover:underline text-sm"
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </AiConfigTable>

        {editing && (
          <div className="bg-white p-4 rounded-lg border border-gray-200 space-y-3">
            <h3 className="font-medium">Edit category</h3>
            <input
              placeholder="Key"
              value={editing.key}
              onChange={(e) => setEditing((x) => (x ? { ...x, key: e.target.value } : null))}
              className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
            />
            <input
              placeholder="Label"
              value={editing.label}
              onChange={(e) => setEditing((x) => (x ? { ...x, label: e.target.value } : null))}
              className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
            />
            <textarea
              placeholder="Description"
              value={editing.description ?? ''}
              onChange={(e) => setEditing((x) => (x ? { ...x, description: e.target.value } : null))}
              className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
              rows={2}
            />
            <div>
              <label className="block text-sm text-gray-600 mb-1">Document types (empty = all)</label>
              <div className="flex flex-wrap gap-2">
                {DOCUMENT_TYPES.map((dt) => (
                  <label key={dt} className="flex items-center gap-1 text-sm">
                    <input
                      type="checkbox"
                      checked={(editing.documentTypes ?? []).includes(dt)}
                      onChange={() =>
                        setEditing((x) =>
                          x
                            ? {
                                ...x,
                                documentTypes: toggleDocType(dt, x.documentTypes ?? []),
                              }
                            : null,
                        )
                      }
                    />
                    {dt}
                  </label>
                ))}
              </div>
            </div>
            <div className="flex gap-2 pt-2">
              <button
                onClick={() => handleUpdate(String(editing.id))}
                className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded"
              >
                Save
              </button>
              <button onClick={() => setEditing(null)} className="px-3 py-1.5 text-sm bg-gray-200 rounded">
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    </AiConfigTabShell>
  );
}
